import { Path, Node, Transforms, Editor, createEditor } from 'slate';
import { cloneDeep, isEqual } from 'lodash';
import { normalizeAnnotations } from '../plugins/MetadataNormalizationPlugin/AnnotationsNormalizationPlugin';
import { normalizeTableOfContentsEnabled } from '../plugins/MetadataNormalizationPlugin/TableOfContentsNormalization';
import Normalization from '../normalization';

/**
 * Returns true if a change to changedPath requires an adjustment to
 * path. For example, if changedPath removed a node before path, path
 * will need to be adjusted to point to the correct location.
 *
 * @param {Array<number>} changedPath The path changed by the operation we're transforming against
 * @param {Array<number>} path The path we may need to adjust
 * @param {boolean} affectsEqualPaths If our path was changed, do we need to adjust?
 * @return {boolean}
 */
const canAffectPath = (changedPath, path, affectsEqualPaths) => {
  // If a node's parent is one of our ancestor, and the node is at a
  // position before us, we'll need to be adjusted.
  return (
    (Path.isAncestor(changedPath.slice(0, -1), path) &&
      changedPath[changedPath.length - 1] < path[changedPath.length - 1]) ||
    Path.isAncestor(changedPath, path) ||
    (Path.equals(changedPath, path) && affectsEqualPaths)
  );
};

/**
 * Returns true if a split_node operation can affect our path, false
 * otherwise. This is different than canAffectPath, because we need to
 * check one level deeper, to `position` / `offset.`
 *
 * @param {Array<number>} path The path we may need to adjust
 * @param {Array<number>} position The position or offset of our node
 * @param {Operation} op The split_node op we're transforming against
 * @param {boolean} affectsEqualPaths If our path was split at our position, do we need to adjust?
 * @return {boolean}
 */
const splitCanAffectPath = (path, position, op, affectsEqualPaths) => {
  const splitAfterUs =
    Path.isAncestor(op.path, path) && path[op.path.length] < op.position;
  const splitParentBeforeUs =
    path.length > op.path.length && op.position <= path[op.path.length];
  const splitUsBeforeOurPosition =
    path.length === op.path.length &&
    (op.position < position || (op.position === position && affectsEqualPaths));

  return (
    !splitAfterUs &&
    canAffectPath(
      op.path,
      path,
      splitParentBeforeUs || splitUsBeforeOurPosition
    )
  );
};

/**
 * Adjust the path component at index `index` by `amount`. For
 * example, to turn [0, 0, 1, 1] into [0, 0, 2, 1], you'd call
 * `adjustPath([0, 0, 1, 1], 1, 2)`
 *
 * @param {Array<number>} path The path that needs adjusting
 * @param {number} amount The amount (positive or negative) to adjust the path by
 * @param {number} index The index of path that will be adjusted by amount
 * @returns {Array<number>}
 */
const adjustPath = (path, amount, index) => {
  const newPath = [...path];
  newPath[index] += amount;
  return newPath;
};

/**
 * True if removing `removedPath` would also remove `path`, false
 * otherwise.
 *
 * @param {Array<number>} path Our path
 * @param {Array<number>} removedPath The path that was removed
 */
const pathWasRemoved = (path, removedPath) =>
  Path.isAncestor(removedPath, path) || Path.equals(removedPath, path);

/**
 * Converts offset and length into start and end, to more easily
 * compare boundaries.
 * @param {number} offset
 * @param {number} length
 * @returns {{start: number, end: number, length: number}}
 */
const bounds = (offset, length) => {
  return {
    start: offset,
    end: offset + length,
    length,
  };
};

/**
 * Get the start and end positions of an insert_text / remove_text operation.
 * @param {Operation} op
 * @returns {{start: number, end: number, length: number}}
 */
const textOpBounds = (op) => bounds(op.offset, op.text.length);

/**
 * Given two {start, end} boundaries, returns the {start, end}
 * boundary of the intersection of the two. For example, given {start:
 * 1, end: 4} and {start: 2, end: 6}, will return {start: 2, end: 4}.
 * Returns `undefined` if the boundaries don't intersect.
 *
 * @param {{start: number, end: number, length: number}} bounds1
 * @param {{start: number, end: number, length: number}} bounds2
 * @returns ({{start: number, end: number, length: number}}|undefined)
 */
const clipBounds = (bounds1, bounds2) => {
  const intersectingBounds = {
    start: Math.max(bounds1.start, bounds2.start),
    end: Math.min(bounds1.end, bounds2.end),
  };
  if (intersectingBounds.start > intersectingBounds.end) return;
  intersectingBounds.length = intersectingBounds.end - intersectingBounds.start;
  return intersectingBounds;
};

// Marks passed in set_mark ops are { type: <string>, data:
// <Immutable.Map> }, Marks passed in insert/remove_mark ops are Mark
// objects, etc.  so we need a custom equals function to compare them.
function equalMarks(mark, otherMark) {
  return mark.type === otherMark.type && isEqual(mark.data, otherMark.data);
}

// newPath on Slate's move operations act differently if you're moving
// nodes around underneath a parent, vs. moving nodes between
// subtrees. For example, if you're moving node [0, 3] to [0, 6], the
// node will end up at [0, 6]. But if you're moving node [0, 3] to [0,
// 4, 1], the node will actually end up at [0, 3, 1]. In one case,
// newPath points at the new position of the node -- in the other
// case, it points at the new position *before* the node was removed.
//
// Handling the difference between these two situations require a
// bunch of flaky edge case handling. Instead, if we treat every
// newPath as pointing to "the target position before the node is
// removed", the code works more consistently, and is easier to reason
// about.
const moveNodeOpWithConsistentTargetPath = (op) => {
  const tOp = { ...op };

  if (
    Path.equals(op.path.slice(0, -1), op.newPath.slice(0, -1)) &&
    canAffectPath(op.path, op.newPath, true)
  ) {
    tOp.newPath = adjustPath(op.newPath, 1, op.path.length - 1);
  }
  return tOp;
};

// This inverses moveNodeOpWithConsistentTargetPath, to make the op
// apply correctly to a Slate document. If you called
// moveNodeOpWithConsistentTargetPath, you must call this before you
// return the op from your function.
const moveNodeOpWithSlateTargetPath = (op) => {
  const tOp = { ...op };
  if (
    Path.equals(op.path.slice(0, -1), op.newPath.slice(0, -1)) &&
    canAffectPath(op.path, op.newPath, false)
  ) {
    tOp.newPath = adjustPath(op.newPath, -1, op.path.length - 1);
  }
  return tOp;
};

const adjustPathForNodeSplit = (path, position, op, affectsEqualPaths) => {
  let adjustedPath = [...path];
  if (splitCanAffectPath(path, position, op, affectsEqualPaths)) {
    adjustedPath = adjustPath(path, 1, op.path.length - 1);
  }

  if (Path.isAncestor(op.path, path)) {
    if (op.position > path[op.path.length]) return adjustedPath;

    // In our hierarchy, we can imagine a node being inserted
    // before our ancestor, and a bunch of nodes moved to that new
    // node. So we have to adjust our path to match the new
    // situation. Check out the tests for an example.
    return adjustPath(adjustedPath, -op.position, op.path.length);
  }
  return adjustedPath;
};

const adjustPositionForNodeSplit = (path, position, op, affectsEqualPaths) => {
  if (Path.equals(path, op.path)) {
    if (position < op.position) return position;
    if (position === op.position && !affectsEqualPaths) return position;
    return position - op.position;
  }
  return position;
};

const adjustNodePathForNodeMove = (path, op) => {
  if (Path.equals(op.path, op.newPath)) return path;
  let adjustedPath = path;

  op = moveNodeOpWithConsistentTargetPath(op);

  let actualNewPath = op.newPath;

  if (canAffectPath(op.path, op.newPath, false)) {
    actualNewPath = adjustPath(op.newPath, -1, op.path.length - 1);
  }

  // If the move operation moved our node (or an ancestor of our
  // node), we modify the path to point at the new path, and don't
  // allow newPath to affect our path.
  const nodeMoved =
    Path.isAncestor(op.path, path) || Path.equals(op.path, path);

  if (nodeMoved) {
    return actualNewPath.concat(path.slice(op.path.length));
  }

  if (canAffectPath(op.path, adjustedPath, false)) {
    adjustedPath = adjustPath(adjustedPath, -1, op.path.length - 1);
  }

  if (canAffectPath(actualNewPath, adjustedPath, true)) {
    adjustedPath = adjustPath(adjustedPath, 1, op.newPath.length - 1);
  }

  return adjustedPath;
};

const adjustPositionForNodeMove = (path, op, affectsEqualPaths = true) => {
  if (Path.equals(op.path, op.newPath)) return path;
  let adjustedPath = path;

  op = moveNodeOpWithConsistentTargetPath(op);

  let actualNewPath = op.newPath;

  if (canAffectPath(op.path, op.newPath, false)) {
    actualNewPath = adjustPath(op.newPath, -1, op.path.length - 1);
  }

  // If the move operation moved our node (or an ancestor of our
  // node), we modify the path to point at the new path, and don't
  // allow newPath to affect our path.
  const nodeMoved = Path.isAncestor(op.path, path);

  if (nodeMoved) {
    return actualNewPath.concat(path.slice(op.path.length));
  }

  if (canAffectPath(op.path, adjustedPath, false)) {
    adjustedPath = adjustPath(adjustedPath, -1, op.path.length - 1);
  }

  if (canAffectPath(actualNewPath, adjustedPath, affectsEqualPaths)) {
    adjustedPath = adjustPath(adjustedPath, 1, op.newPath.length - 1);
  }

  return adjustedPath;
};

const adjustPathForNodeMerge = (path, op, affectsEqualPaths = true) => {
  const parentLength = op.path.length;
  let adjustedPath = [...path];

  if (canAffectPath(op.path, path, affectsEqualPaths)) {
    adjustedPath = adjustPath(adjustedPath, -1, parentLength - 1);
  }

  if (Path.isAncestor(op.path, path)) {
    // We're merging a direct parent, so we need to adjust for the new
    // nodes we're adding
    adjustedPath = adjustPath(adjustedPath, op.position, parentLength);
  }
  return adjustedPath;
};

const adjustPositionForNodeMerge = (path, position, op) => {
  if (Path.equals(path, op.path)) {
    return position + op.position;
  }
  return position;
};

const adjustPathForNodeInsert = (path, op, affectsEqualPaths) => {
  if (canAffectPath(op.path, path, affectsEqualPaths)) {
    return adjustPath(path, 1, op.path.length - 1);
  }
  return path;
};

const adjustPathForNodeRemove = (path, op, affectsEqualPaths) => {
  if (canAffectPath(op.path, path, affectsEqualPaths)) {
    return adjustPath(path, -1, op.path.length - 1);
  }
  return path;
};

const splitOpForNodeSplit = (op1, op2) => {
  const opBounds = bounds(op1.offset, op1.length);
  if (opBounds.start < op2.position && opBounds.end > op2.position) {
    const firstPart = { ...op1, length: op2.position - opBounds.start };
    const secondPart = {
      ...op1,
      path: adjustPath(op1.path, 1, op1.path.length - 1),
      offset: 0,
      length: op1.length - firstPart.length,
    };
    return [firstPart, secondPart];
  }
  return [op1];
};

/**
 * Applies `op` to `value`, returning the new `value`.
 *
 * If operations have been transformed, you should always use this
 * function instead of Slate's built-in `editor.applyOperation`,
 * because transformation can get operations into a state that Slate
 * can't handle. If you must use `applyOperation`, you will need to
 * call `fixupOperation` first.
 */
const applyOpToValue = (editor, op) => {
  editor.apply(fixupOperation(editor, op));
  editor.operations = [];
};

const updateRemovedNodeWithOperation = (removeOp, otherOp) => {
  if (!removeOp.node) return;
  if (!removeOp.value) return removeOp.node;

  const updatedValue = applyOpToValue(removeOp.value, otherOp);
  return updatedValue.document.assertNode(removeOp.path);
};

const fixupRemoveNodeOp = (removeOp, editor) => {
  return { ...removeOp, node: Node.get(editor, removeOp.path) };
};

const comparePaths = (path1, path2) => Path.compare(path1, path2);

const merge = (object, newData) => {
  return Object.assign({}, object, newData);
};

const isEmpty = (object) => {
  return Object.keys(object).length === 0;
};

export const deleteIn = (record, keypath) => {
  let value = record;
  for (const key of keypath.slice(0, -1)) {
    if (!value.hasOwnProperty(key)) return record;
    value = value[key];
  }

  delete value[keypath[keypath.length - 1]];

  return record;
};

export const setIn = (record, keypath, newValue) => {
  let value = record;
  for (const key of keypath.slice(0, -1)) {
    if (!value.hasOwnProperty(key)) value[key] = {};
    value = value[key];
  }

  value[keypath[keypath.length - 1]] = newValue;

  return record;
};

/**
 * Same as `record.setIn(keypath, newValue)`, except when newValue
 * is falsy or an empty object, deletes the object at `keypath` instead.
 */
const updateOrRemove = (record, keypath, newValue) => {
  if (!newValue || isEmpty(newValue)) {
    return deleteIn(cloneDeep(record), keypath);
  } else {
    return setIn(cloneDeep(record), keypath, newValue);
  }
};

/**
 * Deletes all keys in `object` with undefined values, making things cleaner.
 */
const removeUndefined = (object) => {
  Object.keys(object).forEach((key) => {
    if (typeof object[key] === 'undefined') {
      delete object[key];
    }
  });
  return object;
};

// Find a valid text node to move the cursor into, either
// the one preceding or following the removed node.

const fixupCursorPosition = (cursor, cursorDetails, editor) => {
  if (!cursorDetails.removed) return cursor;

  delete cursorDetails.removed;

  const texts = Array.from(Node.texts(editor));
  if (texts.length === 0) {
    console.warn("Couldn't find a valid text node in document");
    Transforms.deselect(editor);
    return;
  }

  const previousTexts = [];
  for (const textEntry of texts) {
    const [, textPath] = textEntry;
    const result = comparePaths(textPath, cursor.path);
    if (result !== -1) {
      break;
    }

    previousTexts.push(textEntry);
  }

  if (previousTexts.length > 0) {
    const [previousText, previousTextPath] =
      previousTexts[previousTexts.length - 1];
    return merge(cursor, {
      path: previousTextPath,
      offset: previousText.text.length,
    });
  } else {
    const [, nextTextPath] = texts[0];
    return merge(cursor, {
      path: nextTextPath,
      offset: 0,
    });
  }
};

const changeRange = (range, data, changePoint) => {
  if (!range) return;

  const { anchor, focus } = range;
  const anchorDetails = { removed: data.anchorRemoved };
  const focusDetails = { removed: data.focusRemoved };

  const newRange = {};

  // We may be modifying paths, so we must set key to null. That will
  // trigger Slate to regenerate it from path.
  if (anchor && anchor.path) {
    newRange.anchor = changePoint(anchor, anchorDetails);
  }

  if (focus && focus.path) {
    newRange.focus = changePoint(focus, focusDetails);
  }

  data.anchorRemoved = anchorDetails.removed;
  data.focusRemoved = focusDetails.removed;
  removeUndefined(data);

  return merge(range, newRange);
};

const fixupOpRange = (rangeOp, key, value) => {
  const extraData = rangeOp.data[key] || {};
  const fixedRange = changeRange(
    rangeOp[key],
    extraData,
    (cursor, cursorDetails) => fixupCursorPosition(cursor, cursorDetails, value)
  );

  const tOp = { ...rangeOp, [key]: fixedRange };
  return updateOrRemove(tOp, ['data', key], extraData);
};

const fixupSetSelectionOp = (op, value) => {
  let tOp = op;
  tOp = fixupOpRange(tOp, 'properties', value);
  tOp = fixupOpRange(tOp, 'newProperties', value);

  return tOp;
};

const fixupSetAnnotationOp = (op, value) => {
  let tOp = op;
  tOp = fixupOpRange(tOp, 'properties', value);
  tOp = fixupOpRange(tOp, 'newProperties', value);

  return tOp;
};

const fixupAnnotationOp = (op, editor) =>
  fixupOpRange(op, 'annotation', editor);

const fixupOperation = (editor, op) => {
  let fixedOp = op;

  if (op.type === 'set_selection') {
    fixedOp = fixupSetSelectionOp(op, editor);
  } else if (op.type === 'remove_node') {
    fixedOp = fixupRemoveNodeOp(op, editor);
  } else if (op.annotation) {
    fixedOp = fixupAnnotationOp(op, editor);
  } else if (op.type === 'set_annotation') {
    fixedOp = fixupSetAnnotationOp(op, editor);
  }

  return fixedOp;
};

const fixupOperations = (editor, ops) => {
  return ops.map((op, index) => {
    const fixedOp = fixupOperation(editor, op);
    if (index !== ops.length - 1) {
      applyOpToValue(editor, fixedOp);
    }
    return fixedOp;
  });
};

const operationHistory = (snapshot, leftOps, rightOps, tLeftOps, tRightOps) => {
  let string = `  start: ${JSON.stringify(snapshot)}
  left ops: ${JSON.stringify(leftOps)}
  right ops: ${JSON.stringify(rightOps)}`;

  if (tLeftOps) {
    string += `\n  transformed left ops: ${JSON.stringify(tLeftOps)}`;
  }

  if (tRightOps) {
    string += `\n  transformed right ops: ${JSON.stringify(tRightOps)}`;
  }

  return string;
};

const emptyParagraph = () => ({
  type: 'paragraph',
  children: [{ text: '' }],
});

const snapshotEditor = (editor) => {
  const snapshot = createEditor();
  const { normalize } = snapshot;
  Normalization.initializeNormalization(snapshot);

  snapshot.normalize = (options = {}) => {
    normalizeAnnotations(snapshot);
    normalizeTableOfContentsEnabled(snapshot);
    normalize(options);
  };

  if (editor) {
    snapshot.children = cloneDeep(editor.children);
  } else {
    snapshot.children = [emptyParagraph()];
  }

  if (editor && editor.metadata) {
    snapshot.metadata = cloneDeep(editor.metadata);
  } else {
    snapshot.metadata = { annotations: {}, tableOfContentsEnabled: false };
  }

  makeAnnotationRangeRefs(snapshot);

  return snapshot;
};

function makeAnnotationRangeRefs(editor) {
  if (!editor.metadata?.annotations) return;
  const { annotations } = editor.metadata;
  for (const [key, annotation] of Object.entries(annotations)) {
    const { range } = annotation;
    // If we don't have a range, we should not create a rangeRef
    if (!range?.current) {
      continue;
    }
    annotations[key].range = Editor.rangeRef(editor, range.current, {
      affinity: range.affinity,
    });
  }
}

export {
  canAffectPath,
  adjustPath,
  pathWasRemoved,
  bounds,
  textOpBounds,
  clipBounds,
  merge,
  equalMarks,
  moveNodeOpWithConsistentTargetPath,
  moveNodeOpWithSlateTargetPath,
  adjustNodePathForNodeMove,
  adjustPositionForNodeMove,
  adjustPathForNodeSplit,
  adjustPositionForNodeSplit,
  adjustPathForNodeMerge,
  adjustPositionForNodeMerge,
  adjustPathForNodeInsert,
  adjustPathForNodeRemove,
  splitOpForNodeSplit,
  applyOpToValue,
  snapshotEditor,
  updateOrRemove,
  updateRemovedNodeWithOperation,
  fixupRemoveNodeOp,
  fixupCursorPosition,
  fixupSetSelectionOp,
  fixupOperations,
  changeRange,
  operationHistory,
  makeAnnotationRangeRefs,
};
